import type { CSSProperties, ReactNode } from "react";
import { useEmbedTokens } from "../theme";
import { useContainerWidth } from "../use-container-width";

/** Container width below which the action drops under the title. */
const ACTION_STACK_BELOW_PX = 480;

interface Props {
  title: string;
  description?: ReactNode;
  /** Heading level; 2 by default because the host page owns the h1. */
  level?: 1 | 2;
  /** One primary action, right-aligned beside the title. */
  action?: ReactNode;
  /** Secondary line under the description (for example a count or a plan name). */
  meta?: ReactNode;
  /** Mark the heading as the programmatic focus target of a section change. */
  focusTarget?: boolean;
}

/**
 * A section's title, description and one action. When `focusTarget` is set the
 * heading carries `data-section-heading` and `tabIndex={-1}`, so the shell can
 * move focus to it after a section change without adding a tab stop.
 */
export function EmbedPageHeader({ title, description, level = 2, action, meta, focusTarget = false }: Props) {
  const t = useEmbedTokens();
  const [ref, width] = useContainerWidth();
  const stacked = width !== null && width > 0 && width < ACTION_STACK_BELOW_PX;
  const Tag = level === 1 ? "h1" : "h2";
  const headingStyle: CSSProperties = {
    margin: 0, color: t.colors.text, lineHeight: t.lineHeight.tight,
    fontSize: level === 1 ? t.fontSize.xl : t.fontSize.lg, fontWeight: t.fontWeight.medium,
    overflowWrap: "anywhere", outline: "none",
  };
  const copyStyle: CSSProperties = {
    margin: `${t.spacing.xs}px 0 0`, color: t.colors.textSecondary,
    fontSize: t.fontSize.sm, lineHeight: t.lineHeight.normal,
  };
  const focusProps = focusTarget ? { "data-section-heading": "", tabIndex: -1 } : {};
  return (
    <div
      ref={ref}
      style={{
        display: "flex", flexDirection: stacked ? "column" : "row", gap: t.spacing.md,
        alignItems: stacked ? "stretch" : "flex-start", justifyContent: "space-between",
        fontFamily: t.fontFamily, marginBottom: t.spacing.lg,
      }}
    >
      <div style={{ minWidth: 0, flex: "1 1 auto" }}>
        <Tag style={headingStyle} {...focusProps}>{title}</Tag>
        {description && <p style={copyStyle}>{description}</p>}
        {meta && <div style={{ ...copyStyle, marginTop: t.spacing.sm }}>{meta}</div>}
      </div>
      {action && <div style={{ flex: "0 0 auto" }}>{action}</div>}
    </div>
  );
}
